// Command palette — ⌘K / Ctrl+K jumps anywhere in the app.
//
// Visual language mirrors DashboardCustomizeDialog: wide modal,
// gold-accented section headers with count badges, two-column card
// grid per section. Keeps the cmdk fuzzy-search engine underneath.
//
// Phase 1 of the navigation redesign (see NAV_REDESIGN.md).
//
// Search sources:
//   • All tools from the registry at src/data/tools.ts — grouped by
//     life-stage section (Dashboard / Calves / Feeders / Fed Cattle /
//     Beef & Supply / My Operation / Insights / Data & Settings).
//   • Recent tools — the last 5 tool paths the user navigated to,
//     persisted in localStorage. Hidden once the user starts typing.
//   • Regions, states, sale barns — same data the old GlobalSearch
//     used. Reuses useSaleBarns so we don't double-fetch.

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Activity,
  Beef,
  Building2,
  Calculator,
  CalendarClock,
  Clock,
  Crown,
  Droplets,
  Factory,
  Globe,
  LayoutDashboard,
  LineChart,
  MapPin,
  Package,
  Server,
  Shield,
  Sparkles,
  Truck,
  type LucideIcon,
} from "lucide-react";
import {
  TOOLS,
  TOOL_SECTIONS,
  type Tool,
  type ToolSectionId,
} from "@/data/tools";
import { tierForPath } from "@/data/tierAccess";
import { useSaleBarns } from "@/hooks/useSaleBarns";
import { cn } from "@/lib/utils";

const SECTION_ICONS: Record<ToolSectionId, LucideIcon> = {
  dashboard: LayoutDashboard,
  "feeder-market": Truck,
  "fed-cattle": Factory,
  "boxed-beef": Package,
  wasde: CalendarClock,
  "cull-bulls": Beef,
  "cow-calf": Beef,
  slaughter: Activity,
  "drought-forage": Droplets,
  "cattle-cycle": LineChart,
  "my-operation": Calculator,
  insights: Sparkles,
  admin: Server,
  "system-admin": Shield,
};

const RECENT_TOOLS_KEY = "beef-pulse-recent-tools";
const RECENT_TOOLS_MAX = 6;

interface RecentEntry {
  path: string;
  at: number;
}

function loadRecentPaths(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_TOOLS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .sort((a, b) => b.at - a.at)
      .slice(0, RECENT_TOOLS_MAX)
      .map((e) => e.path);
  } catch {
    return [];
  }
}

export function recordToolVisit(path: string) {
  try {
    const raw = localStorage.getItem(RECENT_TOOLS_KEY);
    const current = raw ? (JSON.parse(raw) as RecentEntry[]) : [];
    const deduped = current.filter((e) => e.path !== path);
    const updated = [{ path, at: Date.now() }, ...deduped].slice(
      0,
      RECENT_TOOLS_MAX,
    );
    localStorage.setItem(RECENT_TOOLS_KEY, JSON.stringify(updated));
  } catch {
    // localStorage unavailable — silently skip.
  }
}

// ─── Barn / state / region data (mirrors GlobalSearch taxonomy) ────────────

const REGIONS = [
  { name: "Plains", desc: "CO, KS, MT, ND, NE, NM, OK, SD, TX, WY" },
  {
    name: "Cornbelt+SE",
    desc: "IA, IL, MO, MN, WI, AL, AR, FL, GA, KY, LA, MS, NC, SC, TN, VA, WV",
  },
  { name: "West", desc: "AZ, CA, ID, NV, OR, UT, WA" },
];

const STATES = [
  "AL", "AR", "AZ", "CA", "CO", "FL", "GA", "IA", "ID", "IL", "IN",
  "KS", "KY", "LA", "MN", "MO", "MS", "MT", "NC", "ND", "NE", "NM",
  "NV", "OH", "OK", "OR", "SC", "SD", "TN", "TX", "UT", "VA", "WA",
  "WI", "WV", "WY",
];

const STATE_NAMES: Record<string, string> = {
  AL: "Alabama", AR: "Arkansas", AZ: "Arizona", CA: "California",
  CO: "Colorado", FL: "Florida", GA: "Georgia", IA: "Iowa",
  ID: "Idaho", IL: "Illinois", IN: "Indiana", KS: "Kansas",
  KY: "Kentucky", LA: "Louisiana", MN: "Minnesota", MO: "Missouri",
  MS: "Mississippi", MT: "Montana", NC: "North Carolina",
  ND: "North Dakota", NE: "Nebraska", NM: "New Mexico", NV: "Nevada",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah",
  VA: "Virginia", WA: "Washington", WI: "Wisconsin",
  WV: "West Virginia", WY: "Wyoming",
};

// ─── Presentational sub-components ─────────────────────────────────────────

/**
 * Section header identical in visual language to DashboardCustomizeDialog:
 * small icon + uppercase gold title + hairline divider + count badge.
 */
function SectionHeader({
  icon: Icon,
  label,
  count,
}: {
  icon?: LucideIcon;
  label: string;
  count: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="h-3.5 w-3.5 text-foreground-subtle shrink-0" />}
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-foreground-subtle">
        {label}
      </h3>
      <div className="flex-1 h-px bg-border/60" />
      <span className="text-[10px] text-muted-foreground tabular-nums">
        {count}
      </span>
    </div>
  );
}

/**
 * Tool card — reusable item for both Recent and section rows. Matches
 * the widget-picker card: 4-line max with icon + name + description.
 */
function ToolCard({
  tool,
  onNavigate,
}: {
  tool: Tool;
  onNavigate: (path: string) => void;
}) {
  const Icon = SECTION_ICONS[tool.section] ?? LayoutDashboard;
  // Show a "CHOICE" badge on Choice-tier tools so users can spot
  // premium content at a glance in the palette. We use the existing
  // `tierForPath` helper instead of a new field so the palette always
  // matches route-level tier gating — no chance of drift.
  const toolTier = tierForPath(tool.path);
  const isChoice = toolTier === "choice";
  return (
    <CommandItem
      value={`${tool.name} ${tool.description} ${(tool.keywords ?? []).join(" ")}`}
      onSelect={() => onNavigate(tool.path)}
      className={cn(
        "flex items-start gap-3 px-3 py-3 rounded-lg border border-border bg-card text-left cursor-pointer transition-all",
        "hover:bg-muted/50 hover:border-border",
        "data-[selected=true]:bg-primary/5 data-[selected=true]:border-primary/40",
      )}
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-foreground leading-tight truncate">
            {tool.name}
          </p>
          {isChoice && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.12em] shrink-0"
              style={{
                background: "hsl(var(--primary) / 0.14)",
                color: "hsl(var(--primary))",
                border: "1px solid hsl(var(--primary) / 0.35)",
              }}
              title="Choice-tier tool"
            >
              <Crown className="h-2.5 w-2.5" />
              Choice
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">
          {tool.description}
        </p>
      </div>
    </CommandItem>
  );
}

/** Region card — matches ToolCard visual weight. */
function RegionCard({
  region,
  onNavigate,
}: {
  region: { name: string; desc: string };
  onNavigate: (path: string) => void;
}) {
  return (
    <CommandItem
      value={`region ${region.name} ${region.desc}`}
      onSelect={() =>
        onNavigate(`/quick-view/region/${encodeURIComponent(region.name)}`)
      }
      className={cn(
        "flex items-start gap-3 px-3 py-3 rounded-lg border border-border bg-card text-left cursor-pointer transition-all",
        "hover:bg-muted/50",
        "data-[selected=true]:bg-primary/5 data-[selected=true]:border-primary/40",
      )}
    >
      <Globe className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground leading-tight">
          {region.name}
        </p>
        <p className="text-[11px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">
          {region.desc}
        </p>
      </div>
    </CommandItem>
  );
}

/**
 * State chip — 36 states would overwhelm in card-grid format. Compact
 * pill layout: 4-col grid, state name + abbreviation on one line.
 */
function StateChip({
  st,
  onNavigate,
}: {
  st: string;
  onNavigate: (path: string) => void;
}) {
  const fullName = STATE_NAMES[st] ?? st;
  return (
    <CommandItem
      value={`state ${fullName} ${st}`}
      onSelect={() => onNavigate(`/quick-view/state/${encodeURIComponent(st)}`)}
      className={cn(
        "flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border bg-card text-left cursor-pointer transition-all",
        "hover:bg-muted/50",
        "data-[selected=true]:bg-primary/5 data-[selected=true]:border-primary/40",
      )}
    >
      <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="text-xs text-foreground truncate">
        {fullName}{" "}
        <span className="text-muted-foreground font-mono">({st})</span>
      </span>
    </CommandItem>
  );
}

/** Barn card — matches ToolCard visual weight. */
function BarnCard({
  barn,
  onNavigate,
}: {
  barn: { name: string; city: string; state: string };
  onNavigate: (path: string) => void;
}) {
  const stateName = STATE_NAMES[barn.state] ?? barn.state;
  return (
    <CommandItem
      value={`barn ${barn.name} ${barn.city} ${barn.state} ${stateName}`}
      onSelect={() =>
        onNavigate(`/quick-view/barn/${encodeURIComponent(barn.name)}`)
      }
      className={cn(
        "flex items-start gap-3 px-3 py-3 rounded-lg border border-border bg-card text-left cursor-pointer transition-all",
        "hover:bg-muted/50",
        "data-[selected=true]:bg-primary/5 data-[selected=true]:border-primary/40",
      )}
    >
      <Building2 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground leading-tight truncate">
          {barn.name}
        </p>
        <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
          {barn.city}, {barn.state}
        </p>
      </div>
    </CommandItem>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { data: barns = [] } = useSaleBarns();
  const [query, setQuery] = useState("");
  const [recentPaths, setRecentPaths] = useState<string[]>(() =>
    loadRecentPaths(),
  );

  // Refresh recent list + clear query whenever the palette opens.
  useEffect(() => {
    if (open) {
      setRecentPaths(loadRecentPaths());
      setQuery("");
    }
  }, [open]);

  const toolsBySection = useMemo(() => {
    const groups = new Map<ToolSectionId, Tool[]>();
    for (const section of TOOL_SECTIONS) {
      groups.set(
        section.id,
        TOOLS.filter((t) => t.section === section.id),
      );
    }
    return groups;
  }, []);

  const recentTools = useMemo(() => {
    return recentPaths
      .map((p) => TOOLS.find((t) => t.path === p))
      .filter((t): t is Tool => t !== undefined);
  }, [recentPaths]);

  // Alphabetized so producers can scan the list predictably.
  // cmdk filters as the user types, so we don't cap — the whole list
  // is available but irrelevant rows drop out the moment anyone starts
  // typing a barn name.
  const visibleBarns = useMemo(() => {
    return [...barns].sort((a, b) =>
      a.name.localeCompare(b.name, "en", { sensitivity: "base" }),
    );
  }, [barns]);

  function go(path: string) {
    onOpenChange(false);
    // defer navigation to next tick so the dialog closes cleanly
    setTimeout(() => navigate(path), 0);
  }

  const showRecent = query === "" && recentTools.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0 max-h-[85vh]">
        <DialogTitle className="sr-only">
          Command palette — search tools and markets
        </DialogTitle>
        <Command
          className="flex h-full w-full flex-col overflow-hidden bg-popover text-popover-foreground"
          shouldFilter
        >
          <CommandInput
            placeholder="Search tools, barns, states, regions…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-[calc(85vh-6rem)] overflow-y-auto">
            <CommandEmpty className="py-12 px-6 text-center">
              <p className="text-sm font-medium text-foreground mb-1">
                Nothing matches that.
              </p>
              <p className="text-xs text-muted-foreground">
                Try a tool name, a barn, a state, or a region.
              </p>
            </CommandEmpty>

            {/* Recent — only shown when input is empty */}
            {showRecent && (
              <div className="px-6 py-4 border-b border-border">
                <SectionHeader
                  icon={Clock}
                  label="Recent"
                  count={recentTools.length}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {recentTools.map((tool) => (
                    <ToolCard
                      key={`recent-${tool.id}`}
                      tool={tool}
                      onNavigate={go}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="px-6 py-5 space-y-6">
              {/* Tool sections — grouped by life-stage */}
              {TOOL_SECTIONS.map((section) => {
                const tools = toolsBySection.get(section.id) ?? [];
                if (tools.length === 0) return null;
                const Icon = SECTION_ICONS[section.id];
                return (
                  <CommandGroup key={section.id}>
                    <SectionHeader
                      icon={Icon}
                      label={section.label}
                      count={tools.length}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {tools.map((tool) => (
                        <ToolCard
                          key={tool.id}
                          tool={tool}
                          onNavigate={go}
                        />
                      ))}
                    </div>
                  </CommandGroup>
                );
              })}

              {/* Regions — 3 cards */}
              <CommandGroup>
                <SectionHeader
                  icon={Globe}
                  label="Regions"
                  count={REGIONS.length}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {REGIONS.map((r) => (
                    <RegionCard key={r.name} region={r} onNavigate={go} />
                  ))}
                </div>
              </CommandGroup>

              {/* States — compact 4-column grid of chips */}
              <CommandGroup>
                <SectionHeader
                  icon={MapPin}
                  label="States"
                  count={STATES.length}
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                  {STATES.map((st) => (
                    <StateChip key={st} st={st} onNavigate={go} />
                  ))}
                </div>
              </CommandGroup>

              {/* Sale barns */}
              {visibleBarns.length > 0 && (
                <CommandGroup>
                  <SectionHeader
                    icon={Building2}
                    label="Sale barns"
                    count={visibleBarns.length}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {visibleBarns.map((barn) => (
                      <BarnCard
                        key={`${barn.name}-${barn.state}`}
                        barn={barn}
                        onNavigate={go}
                      />
                    ))}
                  </div>
                </CommandGroup>
              )}
            </div>
          </CommandList>

          {/* Keyboard shortcut footer — modern palettes (Linear,
              Raycast, Vercel) always show this. Provides a quiet
              reminder of the navigation grammar without ever
              demanding attention. */}
          <div className="border-t border-border bg-muted/40 px-4 py-2.5 flex items-center justify-between text-[11px] text-foreground-subtle">
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center gap-1.5">
                <kbd className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded bg-card border border-border text-[10px] font-medium tabular-nums shadow-e1">
                  ↵
                </kbd>
                <span>Select</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <kbd className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded bg-card border border-border text-[10px] font-medium shadow-e1">
                  ↑
                </kbd>
                <kbd className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded bg-card border border-border text-[10px] font-medium shadow-e1">
                  ↓
                </kbd>
                <span>Navigate</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <kbd className="inline-flex items-center justify-center h-4 px-1.5 rounded bg-card border border-border text-[10px] font-medium shadow-e1">
                  Esc
                </kbd>
                <span>Close</span>
              </span>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px]">
              Press
              <kbd className="inline-flex items-center justify-center h-4 px-1.5 rounded bg-card border border-border text-[10px] font-medium shadow-e1">
                ⌘K
              </kbd>
              anywhere to open
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

// ─── Global keybinding hook ───────────────────────────────────────────────

/**
 * Hook that mounts the ⌘K / Ctrl+K global keybinding. Attach at the
 * TopNav (or Layout) level — the palette itself is controlled state,
 * so the keybinding just toggles `open`.
 */
export function useCommandPaletteHotkey(setOpen: (open: boolean) => void) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const isK = e.key === "k" || e.key === "K";
      const mod = e.metaKey || e.ctrlKey;
      if (isK && mod) {
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [setOpen]);
}
